import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

function getMondayDieserWoche() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d.toISOString().slice(0, 10);
}

function berechneStunden(beginn, ende) {
  if (!beginn || !ende) return null;
  const [h1, m1] = beginn.split(':').map(Number);
  const [h2, m2] = ende.split(':').map(Number);
  const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (diff <= 0) return null;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

const TAETIGKEIT_MAP = {
  abnahme: 'Abnahme', gemeinde: 'Abstimmung Gemeinde', aufmass: 'Aufmaß',
  hausanschluesse: 'Hausanschlüsse', jourfixe: 'Jour Fixe', kontrolle: 'Kontrolle',
  mitverfolgung: 'Mitverfolgung', planung: 'Planung', rechnungen: 'Rechnungen',
  sonstiges: 'Sonstiges', baufirma: 'Baufirma', zwischenbericht: 'Zwischenbericht',
};

function getTaetigkeiten(json) {
  try {
    const t = JSON.parse(json || '{}');
    return Object.entries(t).filter(([k, v]) => v && k !== 'freitext').map(([k]) => TAETIGKEIT_MAP[k] || k);
  } catch { return []; }
}

function wetterIcon(witterung) {
  if (witterung === 'Sonnig' || witterung === 'Überwiegend klar') return '☀️';
  if (witterung === 'Teilweise bewölkt') return '⛅';
  if (witterung === 'Bedeckt') return '☁️';
  if (witterung === 'Neblig') return '🌫️';
  if (witterung === 'Schneefall') return '❄️';
  if (witterung === 'Gewitter') return '⛈️';
  if (witterung.includes('Regen') || witterung === 'Nieselregen') return '🌧️';
  return '🌤️';
}

const WARNSCHWERE = {
  minor:    { bg: '#fef9c333', color: '#a16207' },
  moderate: { bg: '#fed7aa44', color: '#c2410c' },
  severe:   { bg: '#fee2e244', color: '#b91c1c' },
  extreme:  { bg: '#fae8ff44', color: '#7e22ce' },
};

function WetterWidget({ projektId }) {
  const [wetter, setWetter] = useState(null); // null=lädt, false=kein Wetter, object=Daten

  useEffect(() => {
    if (!projektId) return;
    setWetter(null);
    api.get(`/wetter/${projektId}`)
      .then(setWetter)
      .catch(() => setWetter(false));
  }, [projektId]);

  if (wetter === false) return null;

  if (wetter === null) {
    return (
      <div className="card" style={{ padding: '14px 18px', marginBottom: 16, color: 'var(--text-3)', fontSize: '0.85rem' }}>
        Wetter wird geladen…
      </div>
    );
  }

  const hatBoeen = wetter.boeen_kmh > wetter.wind_kmh;
  // Bei einem Teilausfall der Wetter-API können einzelne Werte fehlen
  const spanne = wetter.temp_min != null && wetter.temp_max != null
    ? ` · ${wetter.temp_min}° / ${wetter.temp_max}°`
    : '';

  return (
    <div className="card" style={{ padding: '14px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>{wetterIcon(wetter.witterung)}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
              {wetter.temperatur != null ? `${wetter.temperatur}°C ` : ''}
              <span style={{ fontWeight: 400, fontSize: '0.9rem', color: 'var(--text-2)' }}>{wetter.witterung}</span>
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>
              {wetter.ort}{spanne}{wetter.veraltet ? ' · zuletzt bekannter Stand' : ''}
            </div>
          </div>
        </div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-2)', textAlign: 'right' }}>
          <div>
            💨 {wetter.wind_kmh} km/h{hatBoeen ? ` · Böen ${wetter.boeen_kmh} km/h` : ''}
          </div>
          {wetter.niederschlag_mm != null && <div>🌧 {wetter.niederschlag_mm} mm Regen</div>}
        </div>
      </div>
      {wetter.warnungen.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {wetter.warnungen.map((w, i) => {
            const stil = WARNSCHWERE[w.schwere] || WARNSCHWERE.minor;
            return (
              <span key={i} style={{ background: stil.bg, color: stil.color, padding: '2px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600 }}>
                ⚠ {w.event}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getBautagebuchTitel(fullName) {
  if (!fullName) return 'Bautagebuch';
  const first = fullName.split(' ')[0];
  const suffix = /[sxzß]$/i.test(first) ? `${first}'` : `${first}s`;
  return `${suffix} Bautagebuch`;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projekte, setProjekte] = useState([]);
  const [berichte, setBerichte] = useState([]);
  const [aktivProjekt, setAktivProjekt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/nutzer/fttbau-projekte').catch(() => []),
      api.get('/berichte'),
      api.get('/nutzer/projekt-sichtbarkeit').catch(() => []),
    ]).then(([p, b, ausgeblendet]) => {
      // Im Admin ausgeblendete Projekte weglassen, Rest alphabetisch sortieren.
      const versteckt = new Set(ausgeblendet);
      const sichtbar = p
        .filter(pr => !versteckt.has(pr.id))
        .sort((a, c) => a.name.localeCompare(c.name, 'de'));
      setProjekte(sichtbar);
      setBerichte(b);
      if (sichtbar.length > 0) {
        const fromId = location.state?.projekt_id;
        setAktivProjekt(sichtbar.find(proj => proj.id === fromId) || sichtbar[0]);
      }
      setLoading(false);
    });
  }, []);

  const allGefiltert = berichte.filter(b => !aktivProjekt || b.projekt_id === aktivProjekt.id);
  const gefilterteBerichte = allGefiltert.filter(b => b.typ !== 'arbeitszeit');
  const mondayStr = getMondayDieserWoche();
  const gefiltArbeitszeiten = allGefiltert.filter(b => b.typ === 'arbeitszeit' && b.datum >= mondayStr);

  const handleNeu = async (typ) => {
    if (!aktivProjekt) return;
    try {
      const b = await api.post('/berichte', { typ, projekt_id: aktivProjekt.id, projekt_name: aktivProjekt.name });
      navigate(`/bericht/${b.id}`);
    } catch (err) { alert(err.message); }
    setModal(null);
  };

  const handleNeuArbeitszeit = async () => {
    if (!aktivProjekt) return;
    try {
      const b = await api.post('/berichte', { typ: 'arbeitszeit', projekt_id: aktivProjekt.id, projekt_name: aktivProjekt.name });
      navigate(`/bericht/${b.id}`);
    } catch (err) { alert(err.message); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '0 20px', display: 'flex', alignItems: 'center', height: 52, gap: 12 }}>
        <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--accent)' }}>{getBautagebuchTitel(user?.name)}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{user?.name}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/karte')} title="Foto-Karte" aria-label="Foto-Karte" style={{ color: 'var(--accent)', padding: '6px 10px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
              <circle cx="12" cy="13" r="3.2" />
            </svg>
          </button>
          {user?.ist_admin && <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin')}>Admin</button>}
          <button className="btn btn-ghost btn-sm" onClick={handleLogout}>Abmelden</button>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
        {/* Projekt-Tabs */}
        {projekte.length > 0 && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap' }}>
            {projekte.map(p => (
              <button key={p.id} onClick={() => setAktivProjekt(p)} className="btn btn-sm"
                style={{ background: aktivProjekt?.id === p.id ? 'var(--accent)' : 'var(--bg-2)', color: aktivProjekt?.id === p.id ? '#fff' : 'var(--text-2)', border: '1px solid var(--border)' }}>
                {p.name}
              </button>
            ))}
          </div>
        )}

        {/* Wetter-Widget */}
        {aktivProjekt && <WetterWidget projektId={aktivProjekt.id} />}

        {/* Aktionen */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
          <button className="btn btn-primary" onClick={() => setModal('neu')} disabled={!aktivProjekt}>
            + Neuer Bericht
          </button>
          <button className="btn btn-secondary" onClick={handleNeuArbeitszeit} disabled={!aktivProjekt}>
            + Arbeitszeit erfassen
          </button>
        </div>

        {/* Berichte-Liste */}
        {loading ? <div className="spinner" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {gefilterteBerichte.length === 0 && gefiltArbeitszeiten.length === 0 && (
              <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
                Noch keine Berichte für dieses Projekt
              </div>
            )}
            {gefilterteBerichte.map(b => (
              <div key={b.id} className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                onClick={() => navigate(`/bericht/${b.id}`)}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    {`${b.datum?.slice(0, 10) || ''}_${b.projekt_name}_${b.typ === 'jourfixe' ? 'Jour Fixe' : 'Zwischenbericht'}`}
                    <span style={{ marginLeft: 10 }}><span className={`badge badge-${b.status}`}>{b.status === 'fertig' ? 'Fertig' : 'Entwurf'}</span></span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>
                    {new Date(b.datum).toLocaleDateString('de-DE')} · {b.erstellt_von_name} · {b.projekt_name}
                  </div>
                </div>
                <div style={{ color: 'var(--text-3)', fontSize: '1.2rem' }}>›</div>
              </div>
            ))}

            {/* Trennlinie + Arbeitszeiten */}
            {gefiltArbeitszeiten.length > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 4px' }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-3)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    Erfasste Arbeitszeit diese Woche
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
                {gefiltArbeitszeiten.map(b => {
                  const dauer = berechneStunden(b.arbeitsbeginn, b.arbeitsende);
                  const tags = getTaetigkeiten(b.taetigkeit);
                  return (
                    <div key={b.id} className="card" style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                      onClick={() => navigate(`/bericht/${b.id}`)}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                          <span style={{ fontWeight: 600 }}>
                            {new Date(b.datum).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </span>
                          {dauer && <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{dauer}</span>}
                          {b.status === 'entwurf' && <span className="badge badge-entwurf">Entwurf</span>}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: tags.length ? 5 : 0 }}>
                          {b.erstellt_von_name}
                        </div>
                        {tags.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {tags.map(l => (
                              <span key={l} style={{ fontSize: '0.72rem', padding: '2px 8px', background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 20, fontWeight: 600 }}>{l}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ color: 'var(--text-3)', fontSize: '1.2rem' }}>›</div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      {/* Modal: Neuer Bericht */}
      {modal === 'neu' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 360 }}>
            <h2 style={{ marginBottom: 20 }}>Neuer Bericht · {aktivProjekt?.name}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button className="btn btn-secondary btn-full" onClick={() => handleNeu('zwischenbericht')}>
                Zwischenbericht
              </button>
              <button className="btn btn-secondary btn-full" onClick={() => handleNeu('jourfixe')}>
                Jour Fixe Protokoll
              </button>
              <button className="btn btn-ghost btn-full" onClick={() => setModal(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
